//NewsLists.tsx
import React, { useState, useEffect } from 'react';
import { getNews } from '../../../../service/HomePage/NewsService';  // データリクエストメソッドを導入
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps, } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import theme from '../../../../style/theme';

export type NewsType = {
  id: number;
  title: string;
  contents: string;
  category: string;
  publishedAt: string;
  isNew: boolean;
};

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row',

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    margin: 0,
  },
  padding: '0px 8px',
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));


/**
 * お知らせ一覧コンポーネント
 * - サーバーからお知らせ情報を取得し、アコーディオン形式で一覧表示する
 * - カテゴリ（情報・警告・重要・緊急）に応じたアイコンを表示する
 * - 新着（isNew）のお知らせには「New」チップを表示する
 */
export default function NewsLists() {

  const [newsList, setNewsList] = useState<NewsType[]>([]);
  const isMobile = useMediaQuery('(max-width:600px)');  // 600px以下の場合はスマートフォンデバイス

  // 初期表示時にサーバーからお知らせデータを取得する
  useEffect(() => {
    const fetchNews = async () => {
      const newsData = await getNews(); // データ取得
      setNewsList(newsData);
    };
    fetchNews();
  }, []);
  const [expanded, setExpanded] = React.useState<string | false>(false);

  // アコーディオンパネルの展開状態を切り替える
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };


  // カテゴリに応じたアイコンを返す
  const getIcon = (category: string) => {
    switch (category) {
      case 'information':
        return <InfoIcon sx={{ color: 'skyblue', fontSize: isMobile ? '1rem' : '1.2rem', marginRight: 1 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'orange', fontSize: isMobile ? '1rem' : '1.2rem', marginRight: 1 }} />;
      case 'important':
        return <LabelImportantIcon sx={{ color: 'gold', fontSize: isMobile ? '1rem' : '1.2rem', marginRight: 1 }} />;
      case 'emergency':
        return <ErrorIcon sx={{ color: 'salmon', fontSize: isMobile ? '1rem' : '1.2rem', marginRight: 1 }} />;
      default:
        return null;
    }
  };


  return (
    <Box
      sx={{
        maxHeight: '100px',
        overflowY: 'auto',
      }}
    >
      {newsList
        .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt))
        .map((news) => {
          // const isNew = dayjs().diff(dayjs(news.publishedAt), 'day') <= 7;  
          return (
            <Accordion
              key={news.id}
              expanded={expanded === String(news.id)}
              onChange={handleChange(String(news.id))}
            >
              <AccordionSummary
                aria-controls={`${news.id}-content`}
                id={`${news.id}-header`}
              >
                <Box sx={{ mt: 1 }}>
                  {getIcon(news.category)}
                </Box>

                {/* Show New label if isNew */}
                <Box sx={{ mr: 1 }}>
                  {news.isNew && (
                    <Chip
                      label="New"
                      size="small"
                      sx={{
                        ml: 1,
                        my: 1,
                        color: "#ffffff",
                        bgcolor: theme.palette.custom.lightRed,
                        maxHeight: '1rem',
                      }}
                    />
                  )}
                </Box>

                <Typography sx={{
                  my: 1,
                  width: isMobile ? 100 : '70%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                  variant='h4'>
                  {news.title}
                </Typography>
                {/* Display published date */}
                <Typography sx={{
                  position: "absolute",
                  right: 40,
                  my: 1,
                  fontStyle: 'italic',
                  color: 'grey',
                }}
                  variant='h4'>
                  {news.publishedAt}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ width: '100%', mb: 1 }}>
                  <Typography variant='body2'>●{news.title}</Typography>
                </Box>
                <Typography variant="body2">{news.contents}</Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}
    </Box>
  );
}